/// <reference types="vite/client" />
/**
 * Web i18n: reuses the mobile translation files (src/i18n/locales) so both
 * apps share one set of strings. Only the namespaces the web needs are
 * bundled, and only the active language (plus the English fallback) is fetched.
 *
 * Language follows the browser; strings not yet translated on the web fall
 * back to English.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getBrowserLocale } from '@sharecost/shared';

export const SUPPORTED_LANGUAGES = [
  'en', 'cs', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt-BR', 'pt-PT', 'ru', 'sk', 'tr', 'uk',
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const FALLBACK_LANGUAGE: SupportedLanguage = 'en';
const LANGUAGE_STORAGE_KEY = 'sharecost_language';

/** Native language names for the switcher (same list as the mobile app). */
export const LANGUAGE_OPTIONS: ReadonlyArray<{ code: SupportedLanguage; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'cs', label: 'Čeština' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'pt-PT', label: 'Português (Portugal)' },
  { code: 'sk', label: 'Slovenčina' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
];
// Every mobile namespace the web references (the codemod reused mobile keys where the text matched)
const NAMESPACES = [
  'common', 'auth', 'home', 'groups', 'expenses', 'settlements', 'premium', 'settings',
  'profile', 'onboarding', 'statistics', 'notifications', 'web',
] as const;

type LocaleModule = { default: Record<string, unknown> };

// Lazy chunks keyed by file path; resolved to (language, namespace) below
const localeModules = import.meta.glob<LocaleModule>(
  '../../../../src/i18n/locales/*/{common,auth,home,groups,expenses,settlements,premium,settings,profile,onboarding,statistics,notifications,web}.json'
);

const LOCALE_PATH_PATTERN = /locales\/([^/]+)\/([^/]+)\.json$/;

function findLoader(language: string, namespace: string): (() => Promise<LocaleModule>) | undefined {
  for (const [path, loader] of Object.entries(localeModules)) {
    const match = LOCALE_PATH_PATTERN.exec(path);
    if (match && match[1] === language && match[2] === namespace) return loader;
  }
  return undefined;
}

function readStoredLanguage(): SupportedLanguage | null {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return SUPPORTED_LANGUAGES.find(lang => lang === stored) ?? null;
  } catch {
    return null; // storage blocked (private mode) -> browser language
  }
}

/** Picks the supported language closest to a BCP 47 tag (`cs-CZ` -> `cs`, `pt` -> `pt-BR`). */
export function detectLanguage(tag: string = getBrowserLocale()): SupportedLanguage {
  const stored = readStoredLanguage();
  if (stored) return stored;
  const normalized = tag.replace('_', '-');
  const exact = SUPPORTED_LANGUAGES.find(lang => lang.toLowerCase() === normalized.toLowerCase());
  if (exact) return exact;

  const base = normalized.split('-')[0].toLowerCase();
  if (base === 'pt') return 'pt-BR';
  const byBase = SUPPORTED_LANGUAGES.find(lang => lang === base);
  return byBase ?? FALLBACK_LANGUAGE;
}

async function loadLanguage(language: SupportedLanguage): Promise<void> {
  // One failed chunk (e.g. a stale hashed URL after a deploy) must not block the others
  const results = await Promise.allSettled(
    NAMESPACES.map(async namespace => {
      const loader = findLoader(language, namespace);
      if (!loader) return; // namespace not translated for this language -> English fallback
      const module = await loader();
      i18next.addResourceBundle(language, namespace, module.default, true, true);
    })
  );
  for (const result of results) {
    if (result.status === 'rejected') {
      console.error(`[i18n] failed to load a ${language} namespace`, result.reason);
    }
  }
}

let initPromise: Promise<void> | null = null;

/** Initializes i18next once; safe to await from the entry point before rendering. */
export function initI18n(): Promise<void> {
  if (initPromise) return initPromise;

  const language = detectLanguage();
  initPromise = i18next
    .use(initReactI18next)
    .init({
      resources: {},
      lng: language,
      fallbackLng: FALLBACK_LANGUAGE,
      ns: [...NAMESPACES],
      defaultNS: 'common',
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    })
    .then(async () => {
      // English first: it is the fallback for anything the active language fails to load
      await loadLanguage(FALLBACK_LANGUAGE);
      if (language !== FALLBACK_LANGUAGE) {
        await loadLanguage(language);
      }
      // Keep <html lang> in sync for screen readers, hyphenation and browser translation prompts
      i18next.on('languageChanged', (lng) => {
        document.documentElement.lang = lng;
      });
      // Re-render subscribers now that bundles are present
      await i18next.changeLanguage(language);
    });

  return initPromise;
}

/** Switches the UI language and remembers the choice for the next visit. */
export async function setLanguage(language: SupportedLanguage): Promise<void> {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Best effort: the choice simply does not survive a reload
  }
  await loadLanguage(language);
  await i18next.changeLanguage(language);
}

export default i18next;
